import { Form, Formik, FormikConfig } from 'formik';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as yup from 'yup';
import Logo from '../../assets/logo-same-color.svg?react';
import { Button } from '../../components/Button';
import { FormikInput } from '../../components/formik/FormInput';
import { Input } from '../../components/inputs/Input';
import { useLogin } from '../../hooks/modules/user/useLogin';
import { useValidationTranslations } from '../../hooks/useValidationTranslations';
import { T, useT } from '../../translation/src';

const CODE_LENGTH = 6;

type VerifyFailure = 'INVALID_CODE' | 'CODE_EXPIRED' | 'TOO_MANY_ATTEMPTS';

const server = import.meta.env.VITE_NX_SERVER;
// Test environments only: a "dev override" under the verify button skips the mailed code. The server
// honours it only with EMAIL-VERIFICATION-BYPASS=true on its side, so a stray client flag does nothing.
const bypassEnabled = import.meta.env.VITE_NX_EMAIL_VERIFICATION_BYPASS === 'true';

const postJson = (path: string, body: Record<string, unknown>) => fetch(`${server}/auth${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), credentials: 'include' });

const useTranslations = () => ({
  code: useT('verification code', { swc: true }),
  invalidCode: useT('that code is not correct', { swc: true }),
  expired: useT('that code has expired, request a new one', { swc: true }),
  tooManyAttempts: useT('too many attempts, request a new code', { swc: true }),
  sent: useT('a new code is on its way', { swc: true }),
  verified: useT('email address verified, you can sign in now', { swc: true }),
  waitSeconds: useT('you can request a new code in {seconds} seconds', { swc: true }),
});

interface VerificationCodeFormProps {
  email: string;
  /**
   * Known when the user just typed it (registration, login): a verified code then signs them in
   * straight away. Without it the user is sent to the login page after verifying.
   */
  password?: string;
  /** Seconds the server asks to wait before the first resend (a code was just mailed). */
  initialWaitSeconds?: number;
  /** Where a successful sign-in lands: the page the visitor was heading to, if the login page knows it. */
  redirectTo?: string;
  /** Offers "Wrong email address? Change it", which opens the change screen (see VerificationScreen). */
  onChangeEmail?: () => void;
}

/**
 * Entry of the 6-digit code from the verification mail. Shown right after registration and on a
 * login attempt against a still-locked account. The resend cooldown is enforced by the server;
 * the countdown here only mirrors what it reports.
 */
const VerificationCodeForm_: React.FC2<VerificationCodeFormProps> = ({ email, password, initialWaitSeconds = 60, redirectTo = '/', onChangeEmail }) => {
  const translations = useTranslations();
  const { somethingWentWrong, isRequired } = useValidationTranslations();
  const login = useLogin();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [bypassing, setBypassing] = useState(false);
  const [waitSeconds, setWaitSeconds] = useState(initialWaitSeconds);

  useEffect(() => {
    if (waitSeconds <= 0) return;
    const timeout = setTimeout(() => setWaitSeconds((seconds) => seconds - 1), 1000);
    return () => clearTimeout(timeout);
  }, [waitSeconds]);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        code: yup
          .string()
          .trim()
          .matches(new RegExp(`^\\d{${CODE_LENGTH}}$`), translations.invalidCode)
          .required(isRequired),
      }),
    [isRequired, translations.invalidCode],
  );
  const initialValues = useMemo(() => ({ code: '' }), []);

  // Verified, one way or another: sign in with the password still in hand, else on to the login page.
  const onVerified = useCallback(async () => {
    if (password) {
      const loginResponse = await login({ email, password, remember: true });
      if (loginResponse.status === 200) {
        navigate(redirectTo, { replace: true });
        return;
      }
    }
    toast.success(translations.verified);
    navigate('/login');
  }, [email, password, login, navigate, redirectTo, translations.verified]);

  const onSubmit: FormikConfig<{ code: string }>['onSubmit'] = useCallback(
    async ({ code }, helpers) => {
      setSubmitting(true);
      try {
        const response = await postJson('/verify-email', { email, code: code.trim() });
        if (response.status === 200) {
          await onVerified();
          return;
        }
        const body = (await response.json().catch(() => ({}))) as { message?: VerifyFailure };
        const messages: Record<VerifyFailure, string> = { INVALID_CODE: translations.invalidCode, CODE_EXPIRED: translations.expired, TOO_MANY_ATTEMPTS: translations.tooManyAttempts };
        helpers.setFieldError('code', (body.message && messages[body.message]) || somethingWentWrong);
      } catch {
        toast.error(somethingWentWrong);
      } finally {
        setSubmitting(false);
      }
    },
    [email, onVerified, translations, somethingWentWrong],
  );

  const onBypass = useCallback(async () => {
    setBypassing(true);
    try {
      const response = await postJson('/verify-email/bypass', { email });
      if (response.status === 200) {
        await onVerified();
        return;
      }
      toast.error(somethingWentWrong);
    } catch {
      toast.error(somethingWentWrong);
    } finally {
      setBypassing(false);
    }
  }, [email, onVerified, somethingWentWrong]);

  const onResend = useCallback(async () => {
    setResending(true);
    try {
      const response = await postJson('/resend-verification', { email });
      if (response.status === 200) {
        toast.success(translations.sent);
        setWaitSeconds(60);
        return;
      }
      if (response.status === 429) {
        const body = (await response.json().catch(() => ({}))) as { retryAfterSeconds?: number };
        setWaitSeconds(body.retryAfterSeconds ?? 60);
        return;
      }
      toast.error(somethingWentWrong);
    } catch {
      toast.error(somethingWentWrong);
    } finally {
      setResending(false);
    }
  }, [email, translations.sent, somethingWentWrong]);

  return (
    <>
      <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
        <Form className="mt-8">
          <FormikInput autoFocus name="code" inputMode="numeric" autoComplete="one-time-code" maxLength={CODE_LENGTH} label={translations.code} placeholder="123456" />
          <Button variant="primary" type="submit" className="items-center justify-center w-full" loading={submitting}>
            <T _str="verify" swc />
          </Button>
          {bypassEnabled && (
            <Button variant="superAdmin" size="sm" className="items-center justify-center w-full mt-2" onClick={onBypass} loading={bypassing}>
              <T _str="dev override: skip verification" swc />
            </Button>
          )}
          <div className="flex flex-wrap items-center justify-center gap-1 mt-4 text-sm">
            <span>
              <T _str="Didn't receive the email? Check your spam folder, or" />
            </span>
            {waitSeconds > 0 ? (
              <span className="text-gray-500 dark:text-gray-400">{translations.waitSeconds.replace('{seconds}', String(waitSeconds))}</span>
            ) : (
              <Button variant="tertiary" size="xs" onClick={onResend} loading={resending}>
                <span className="whitespace-nowrap">
                  <T _str="send a new code" swc />
                </span>
              </Button>
            )}
          </div>
        </Form>
      </Formik>
      {onChangeEmail && (
        <p className="mt-4 text-sm text-center">
          <T _str="Wrong email address?" />{' '}
          <button type="button" onClick={onChangeEmail} className="font-medium text-french-violet-500 hover:text-french-violet-400 dark:text-neon-green-400 dark:hover:text-neon-green-300">
            <T _str="change it" />
          </button>
        </p>
      )}
    </>
  );
};

type ChangeFailure = 'INVALID_EMAIL' | 'ALREADY_EXISTS' | 'NOT_PENDING';

// The change screen's form: the right address, and the account moves there with a new code.
const ChangeEmailForm: React.FC<{ email: string; password: string; onChanged: (email: string) => void; onCancel: () => void }> = ({ email, password, onChanged, onCancel }) => {
  const { somethingWentWrong, validEmail } = useValidationTranslations();
  const newEmailLabel = useT('email address', { swc: true });
  const taken = useT('an account with this email address already exists', { swc: true });
  const notPending = useT('this account is already verified, sign in instead', { swc: true });
  const sentTo = useT('a new code is on its way to {email}', { swc: true });
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  const onSubmit = useCallback(
    async (event?: React.FormEvent) => {
      event?.preventDefault();
      const newEmail = value.trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) return setError(validEmail);
      setSaving(true);
      setError(undefined);
      try {
        const response = await postJson('/change-verification-email', { email, password, newEmail });
        if (response.status === 200) {
          const body = (await response.json().catch(() => ({}))) as { email?: string };
          toast.success(sentTo.replace('{email}', body.email ?? newEmail));
          onChanged(body.email ?? newEmail);
          return;
        }
        const body = (await response.json().catch(() => ({}))) as { message?: ChangeFailure };
        const messages: Record<ChangeFailure, string> = { INVALID_EMAIL: validEmail, ALREADY_EXISTS: taken, NOT_PENDING: notPending };
        setError((body.message && messages[body.message]) || somethingWentWrong);
      } catch {
        setError(somethingWentWrong);
      } finally {
        setSaving(false);
      }
    },
    [email, notPending, onChanged, password, sentTo, somethingWentWrong, taken, validEmail, value],
  );

  return (
    <form className="mt-8 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
      <div className="flex flex-col gap-1">
        <label htmlFor="change-verification-email" className="text-sm font-medium">
          {newEmailLabel}
        </label>
        <Input id="change-verification-email" type="email" autoComplete="email" autoFocus value={value} onChange={(event) => setValue(event.target.value)} className={error ? 'input-error' : undefined} />
        {error && <span className="text-xs text-error-red-500">{error}</span>}
      </div>
      <Button variant="primary" type="submit" className="items-center justify-center w-full" loading={saving}>
        <T _str="send the code to this address" swc />
      </Button>
      <p className="text-sm text-center">
        <button type="button" onClick={onCancel} className="font-medium text-french-violet-500 hover:text-french-violet-400 dark:text-neon-green-400 dark:hover:text-neon-green-300">
          <T _str="back to the code" swc />
        </button>
      </p>
    </form>
  );
};

// The address as the verification screens show it: part of the sentence, a little stronger, never split.
const Address: React.FC<{ email: string }> = ({ email }) => <span className="inline-block max-w-full break-all font-semibold text-french-violet-500 dark:text-neon-green-400">{email}</span>;

interface VerificationScreenProps extends Omit<VerificationCodeFormProps, 'onChangeEmail'> {
  /** The sentence around the address, before and after it. */
  before: React.ReactNode;
  after: React.ReactNode;
  /** The way out under the form: back to login, or another account. */
  footer: React.ReactNode;
  /** The address was corrected: the page keeps the new one. Offered only with the password. */
  onEmailChanged?: (email: string) => void;
}

/**
 * The whole verification page: the code screen, and — for a typo at sign-up — its own screen to change the address,
 * after which the code screen is back with the new address and a code already on its way there.
 */
const VerificationScreen_: React.FC<VerificationScreenProps> = ({ email, password, before, after, footer, onEmailChanged, initialWaitSeconds, redirectTo }) => {
  const [changing, setChanging] = useState(false);
  // A code went out with the change: the new code form waits before offering another.
  const [changed, setChanged] = useState(false);
  const canChange = !!password && !!onEmailChanged;

  return (
    <div className="flex items-center justify-center w-full min-h-full px-4 py-12 sm:px-6 lg:px-8 dark:bg-[#313131] dark:text-white">
      <div className="w-full max-w-md space-y-8">
        <div>
          <Logo className="w-auto mx-auto text-eerie-black dark:text-white" />
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-center">{changing ? <T _str="Change your email address" /> : <T _str="Verify your email address" />}</h2>
          <p className="mt-2 text-sm text-center">
            {changing ? (
              <>
                <T _str="Your account now uses" /> <Address email={email} />. <T _str="Enter the right address and we send a new code there." />
              </>
            ) : (
              <>
                {before} <Address email={email} />. {after}
              </>
            )}
          </p>
        </div>
        {changing && password && onEmailChanged ? (
          <ChangeEmailForm
            email={email}
            password={password}
            onCancel={() => setChanging(false)}
            onChanged={(newEmail) => {
              onEmailChanged(newEmail);
              setChanged(true);
              setChanging(false);
            }}
          />
        ) : (
          <VerificationCodeForm key={email} email={email} password={password} initialWaitSeconds={changed ? 60 : initialWaitSeconds} redirectTo={redirectTo} onChangeEmail={canChange ? () => setChanging(true) : undefined} />
        )}
        {!changing && footer}
      </div>
    </div>
  );
};

export const VerificationCodeForm = memo(VerificationCodeForm_);
export const VerificationScreen = memo(VerificationScreen_);
